import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../context/GlobalContext";
import { debugAuth, isDebugMode } from "../services/debugService";
import { config } from "../config/environment";

const apiUrl = config.api.baseUrl;

export default function useAuth() {
  const navigate = useNavigate();
  const { user, setUser } = useGlobalContext();

  const fetchUser = useCallback(async () => {
    const sessionId = localStorage.getItem("sessionId");

    // Handle debug mode
    if (isDebugMode && debugAuth.isDebugSession()) {
      const debugUser = debugAuth.getCurrentUser();
      if (debugUser) {
        setUser(debugUser);
        return;
      }
    }

    if (!sessionId) {
      const error = new Error("No session found");
      error.status = 401;
      throw error;
    }

    try {
      const response = await fetch(`${apiUrl}/auth/user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionId}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("sessionId");
          navigate("/unauthorized", { replace: true });
          return;
        }
        const error = new Error("Request failed");
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error("Error fetching user:", error);

      // When the backend redirects expired-token requests without CORS headers,
      // fetch throws a TypeError (message varies: Chrome "Failed to fetch",
      // Safari "Load failed", Firefox "NetworkError …"). Redirect to clear
      // the stale session.
      if (error instanceof TypeError && sessionId) {
        localStorage.removeItem("sessionId");
        navigate("/unauthorized", { replace: true });
        return;
      }

      // In debug mode, if API fails, don't throw error but provide option to use mock users
      if (isDebugMode) {
        console.warn(
          "API call failed in debug mode - user can use debug panel to mock login",
        );
        return;
      }

      throw error;
    }
  }, [setUser]);

  return { user, fetchUser };
}
